import { useState } from "react";
import { Button } from "../../components/button/button";
import { AiService } from "../../utilities/services/ai-service";
import UserLatestMskScoreService from "../../utilities/services/user-latest-msk-score-service";
import { MskScore } from "../../models/interfaces/msk-score";
import { createLogger } from "../../utilities/logger";

const log = createLogger("MskSummaryTool");

const COMPONENT_CLASS = "c-msk-summary-tool";

const MskSummaryTool: React.FC = () => {
    const [userId, setUserId] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<string>("");

    const handleSubmit = async () => {
        if (!userId || userId.trim().length === 0) {
            return;
        }

        setLoading(true);
        setResult("");

        try {
            log.debug("[MSK Summary Tool] Fetching MSK score for userId", { userRecordId: userId.trim() });

            const mskScoreResults = await UserLatestMskScoreService.getBy(
                [{ field: "userId", operator: "==", value: userId.trim() }],
            );
            log.debug("[MSK Summary Tool] MSK score results", { mskScoreResults });

            const mskScore = mskScoreResults?.[0] as MskScore | undefined;

            if (!mskScore) {
                throw new Error(`No MSK score found for user: ${userId}`);
            }

            log.debug("[MSK Summary Tool] MSK Score retrieved", { mskScore });
            log.debug("[MSK Summary Tool] MSK Score ID", { id: mskScore.id });

            const response = await AiService.mskSummary(mskScore);
            log.debug("[MSK Summary Tool] AI response", { response });
            setResult(response);
        } catch (error: unknown) {
            log.error("[MSK Summary Tool] Error", {
                exception: error,
            });
            const errorMessage = error instanceof Error ? error.message : "Failed to get MSK summary";
            setResult(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={COMPONENT_CLASS}>
            <h2>AI MSK Summary</h2>
            <p>
                This tool retrieves the latest MSK score for a user and sends it to the AI
                for summarization.
            </p>

            <div className={`${COMPONENT_CLASS}__input`}>
                <label>User ID</label>
                <input
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter user ID"
                />
            </div>

            <div className={`${COMPONENT_CLASS}__actions`}>
                <Button
                    onClick={handleSubmit}
                    buttonText={loading ? "Loading..." : "Get MSK Summary"}
                    disabled={loading || !userId || userId.trim().length === 0}
                />
            </div>

            {result && (
                <div className={`${COMPONENT_CLASS}__result`}>
                    <label>Result</label>
                    <pre>{result}</pre>
                </div>
            )}
        </div>
    );
};

export default MskSummaryTool;